import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HiOutlinePlus } from "react-icons/hi2";
import { IClinicApiRow, IClinicTagConfig } from "@/api_framework/api_modals/ClinicTagConfig";
import { useClinicListAPI } from "@/app/hooks/api_hooks/org/useClinicListAPI";
import { useClinicTagConfigAPI } from "@/app/hooks/api_hooks/quick_commerce/useClinicTagConfigAPI";
import { useRouter } from "@/app/hooks/useRouter";
import { BackLink, TileAvatar } from "../HomeTileConfig/components";
import { matchesSearch, primaryButtonClassName, StatusFilter } from "../HomeTileConfig/interface";
import { fromPersonaRule } from "../HomeTileConfig/personaExpression";
import { ClinicList } from "./ClinicList";
import { toClinicOptions } from "./interface";
import { ClinicTagFilters, ClinicTagTable, DeleteTagRuleModal, useClinicTagColumns } from "./ClinicTagTable";

const ClinicTagConfigSetup = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const selectedClinicId = params.get("clinic_id") ?? "";
  const clinicListUrl = getRouteKey("CLINIC_TAG_SETUP", "url");

  const { execute: fetchClinics, clinicList } = useClinicListAPI();
  const { configList, isListLoading, listError, isDeleting, fetchClinicConfigs, deleteConfig } =
    useClinicTagConfigAPI();

  const [isClinicListLoading, setIsClinicListLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [deleteTarget, setDeleteTarget] = useState<IClinicTagConfig | null>(null);

  useEffect(() => {
    let isCurrent = true;
    setIsClinicListLoading(true);
    fetchClinics().finally(() => {
      if (isCurrent) setIsClinicListLoading(false);
    });
    return () => {
      isCurrent = false;
    };
  }, [fetchClinics]);

  const clinics = useMemo(() => toClinicOptions(clinicList as IClinicApiRow[]), [clinicList]);
  const selectedClinic = useMemo(
    () => clinics.find((clinic) => clinic.clinic_id === selectedClinicId),
    [clinics, selectedClinicId],
  );
  const clinicName = selectedClinic?.clinic_name ?? "this clinic";

  const loadConfigurations = useCallback(() => {
    if (selectedClinicId) fetchClinicConfigs(selectedClinicId);
  }, [selectedClinicId, fetchClinicConfigs]);

  useEffect(() => {
    loadConfigurations();
  }, [loadConfigurations]);

  const visibleConfigs = useMemo(
    () =>
      configList.filter((config) => {
        if (statusFilter !== "ALL" && config.status !== statusFilter) return false;
        return matchesSearch(searchTerm, [fromPersonaRule(config.persona_rule), config.chip_txt]);
      }),
    [configList, searchTerm, statusFilter],
  );

  const activeCount = useMemo(() => configList.filter((config) => config.status === "ACTIVE").length, [configList]);

  const goToConfig = useCallback(
    (configId?: string) => {
      const query = new URLSearchParams({ clinic_id: selectedClinicId });
      if (configId) query.set("config_id", configId);
      navigate(`${getRouteKey("CLINIC_TAG_ONBOARD", "url")}?${query.toString()}`);
    },
    [getRouteKey, navigate, selectedClinicId],
  );

  const addRule = useCallback(() => goToConfig(), [goToConfig]);

  const openClinic = useCallback(
    (clinicId: string) => navigate(`${clinicListUrl}?clinic_id=${encodeURIComponent(clinicId)}`),
    [clinicListUrl, navigate],
  );

  const columns = useClinicTagColumns({
    onEdit: goToConfig,
    onDelete: setDeleteTarget,
    statusFilter,
    onStatusFilterChange: setStatusFilter,
  });

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setStatusFilter("ALL");
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (await deleteConfig(deleteTarget.id)) setDeleteTarget(null);
  };

  return (
    <div className="px-5 py-4">
      {selectedClinicId && <BackLink label="Back to clinics" onClick={() => navigate(clinicListUrl)} />}

      <div className="mb-4">
        <h1 className="text-lg font-semibold leading-7 text-gray-900">Clinic Tag Configuration</h1>
      </div>

      <section className="w-full min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {selectedClinicId ? (
          <>
            <div className="flex flex-wrap items-center gap-4 border-b border-slate-100 px-5 py-4">
              <TileAvatar name={selectedClinic?.clinic_name ?? "?"} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-gray-900">
                  {selectedClinic?.clinic_name ?? (isClinicListLoading ? "Loading clinic…" : "Clinic")}
                </div>
                <p className="mt-0.5 text-[11px] text-gray-500">
                  {isListLoading ? "Loading rules…" : `${configList.length} rules · ${activeCount} active`}
                </p>
              </div>
              <button type="button" onClick={addRule} className={primaryButtonClassName}>
                <HiOutlinePlus className="h-4 w-4" />
                Add Rule
              </button>
            </div>
            <ClinicTagFilters searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            <ClinicTagTable
              columns={columns}
              configs={visibleConfigs}
              isLoading={isListLoading}
              error={listError}
              hasFilters={Boolean(searchTerm.trim()) || statusFilter !== "ALL"}
              clinicName={clinicName}
              onRetry={loadConfigurations}
              onClearFilters={clearFilters}
              onAddRule={addRule}
            />
          </>
        ) : (
          <ClinicList clinics={clinics} isLoading={isClinicListLoading} onEdit={openClinic} />
        )}
      </section>

      <DeleteTagRuleModal
        target={deleteTarget}
        clinicName={clinicName}
        isLoading={isDeleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default ClinicTagConfigSetup;
